import React, { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useOrderAdjustmentProductInputQuery } from '../../../../../../../../services';
import { RhSingleAutocomplete } from '../../../../../../../dum';
import {
    IOrderAdjustment,
    ORDER_SALE_PRODUCTS,
} from '../../order-adjustment-form';
import { FIELD_LABELS, shortLabel } from '../../../../../../../../helpers';

const ARRAY_NAME: keyof IOrderAdjustment = 'order_adjustment_products';
const PRODUCT_FIELD_NAME: keyof IOrderAdjustment['order_adjustment_products'][number] =
    'product';
const GROUP_WEIGHT_FIELD_NAME: keyof IOrderAdjustment['order_adjustment_products'][number] =
    'group_weight';
const KILOS_FIELD_NAME: keyof IOrderAdjustment['order_adjustment_products'][number] =
    'kilos';
const GROUPS_FIELD_NAME: keyof IOrderAdjustment['order_adjustment_products'][number] =
    'groups';

interface IProductsInput {
    index: number;
    initialValues: IOrderAdjustment;
}

export function OrderProductionProductInput({
    index,
    initialValues,
}: IProductsInput) {
    const { data: productsData } = useOrderAdjustmentProductInputQuery();
    const INPUT_NAME = `${ARRAY_NAME}.${index}.${PRODUCT_FIELD_NAME}`;
    const formContext = useFormContext();

    // When a sale is attached (devolución, or any adjustment with a linked sale),
    // restrict the pickable products to the ones actually on that sale — a
    // devolución can only return what was sold. With no sale, all products show.
    const orderSaleProducts = useWatch({
        name: ORDER_SALE_PRODUCTS,
    }) as IOrderAdjustment['order_sale_products'];

    const productOptions = useMemo(() => {
        const allProducts = productsData?.getProducts || [];
        if (!orderSaleProducts || orderSaleProducts.length === 0) {
            return allProducts;
        }
        const saleProductIds = new Set(
            orderSaleProducts.map((osp) => osp.product_id),
        );
        return allProducts.filter((product) => saleProductIds.has(product.id));
    }, [productsData, orderSaleProducts]);

    return (
        <RhSingleAutocomplete
            itemText={'compound_description'}
            itemValue={'id'}
            returnObject
            options={productOptions}
            name={INPUT_NAME}
            label={shortLabel(FIELD_LABELS.product_id)}
            disableGutters
            onChange={(product) => {
                const foundInitialOrderAdjustmentProduct =
                    initialValues.order_adjustment_products.find((oap) => {
                        return oap.product?.id === product.id;
                    });
                if (foundInitialOrderAdjustmentProduct) {
                    formContext.setValue(
                        `${ARRAY_NAME}.${index}.id`,
                        foundInitialOrderAdjustmentProduct.id,
                    );
                }

                const orderAdjustmentProducts = formContext.getValues(
                    ARRAY_NAME,
                ) as IOrderAdjustment['order_adjustment_products'];

                orderAdjustmentProducts.forEach((o, i) => {
                    void formContext.trigger(
                        `${ARRAY_NAME}.${i}.${PRODUCT_FIELD_NAME}`,
                    );
                });

                if (productsData && !!product) {
                    const currentProduct = productsData.getProducts.find(
                        (p) => p.id === product.id,
                    );
                    if (currentProduct) {
                        formContext.setValue(
                            `${ARRAY_NAME}.${index}.${GROUP_WEIGHT_FIELD_NAME}`,
                            currentProduct.current_group_weight,
                        );
                        if (product && product.current_group_weight) {
                            const groups =
                                (formContext.getValues(
                                    `${ARRAY_NAME}.${index}.${GROUPS_FIELD_NAME}`,
                                ) as IOrderAdjustment['order_adjustment_products'][number]['groups']) ||
                                0;
                            formContext.setValue(
                                `${ARRAY_NAME}.${index}.${KILOS_FIELD_NAME}`,
                                groups * product.current_group_weight,
                            );
                        }
                    }
                }
            }}
        />
    );
}
